import React, {
  useCallback,
  useMemo,
  useContext,
  useState,
  useEffect,
} from 'react';
import format from 'string-template';
import {
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { compose } from 'recompose';
import { getFirebase } from 'firestorter';

import StarIcon from '@mui/icons-material/Star';
import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/v2/check-circle.svg';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import { getQueryVariable } from '../../../../../../utils/queryParams';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import UserContext from '../../../../../../context/UserContext';
import { CoachingActivity } from '../../../../../../utils/log';
import useLogger from '../../../../../../hooks/useLogger';
import useTimeForActionLogger, { ActionEvent } from '../../../../../../hooks/useTimeForActionLogger';
import { MealPlannerType, getTabPath } from '../../../../../routes/mealPlanner/mealPlannerPages';
import useMealPlanNavigation from '../../../../../hooks/useMealPlanNavigation';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import MealPlanAssignment from '../../../../../Model/MealPlanAssignment';
import { withMealPlanContextReady } from '../../../../../context/MealPlanContext';
import MealPlanAssignmentContext, {
  withMealPlanAssignmentContextProvider,
  withMealPlanAssignmentContextReady,
} from '../../../../../context/MealPlanAssignmentContext';
import ChatButton from '../../../../../components/ChatButton';
import ClientQuickInfoButton from '../../../../../components/ClientQuickInfoButton';
import UserPhysicalAttributes from '../../../components/UserPhysicalAttributes';
import UserNutritionalGoals from '../../../components/UserNutritionalGoals';
import UserMealPlan, { MealPlanAssignOption } from '../../../components/UserMealPlan';
import { InfoTag } from '../../../../../../components/Tags';
import { TitleIconsContainer } from '../../styles';

import {
  Container,
  UserInfoContainer,
  StyledAvatar,
  UserInfoTitle,
  TitleRow,
  SectionContainer,
  FeedbackButton,
  ItemContainer,
  Label,
  ContentContainer,
} from './styles';
import texts from './texts.json';

const MealPlanRefresh = () => {
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [feedbackHandler, setFeedbackHandler] = useState(false);
  // Get refresh user id, redirect to meal plan dashboard if not present
  const user = getQueryVariable('user');

  // Access state from react router to know from where the component was initialized
  const location = useLocation();
  const {
    state: {
      isRoutedFromFeed = false,
    } = {},
  } = location;

  const history = useHistory();

  const isComponentMountedRef = useComponentMounted();
  const {
    authUser: {
      uid: authUserId,
    } = {},
  } = useSessionStore();

  const {
    mealPlanAssignmentDoc = {},
    userDoc = {},
    nutritionProfileDoc = {},
    macroGoalsDoc = {},
    nutritionalGoals = {},
    setNutritionalGoals,
    newMealPlanData = {},
    selectedWeightGoal,
    setSelectedWeightGoal,
    assignmentNote,
    fetchMealPlanAssignmentDoc,
    selectedProductId,
    heldProducts = [],
  } = useContext(MealPlanAssignmentContext);

  const {
    navigateToAssignments,
  } = useMealPlanNavigation();

  const {
    userId: coachId,
  } = useContext(UserContext);

  const {
    id: userId,
    name: userName,
    avatarUrl: userAvatar,
  } = userDoc;

  const assignmentProductName = useMemo(() => (
    heldProducts.find(({ productId }) => productId === selectedProductId)?.productName
    || mealPlanAssignmentDoc?.productName
    || ''
  ), [heldProducts, selectedProductId, mealPlanAssignmentDoc]);

  const [isSaving, setIsSaving] = useState(false);
  const { logCoachingActivity } = useLogger();
  const { logSuccess: logMealPlanAssignmentTime } = useTimeForActionLogger({
    actionEvent: ActionEvent.MEAL_PLAN,
    ready: !!user,
  });

  const renderAvatar = useCallback(() => {
    if (userAvatar) {
      return <StyledAvatar src={userAvatar} />;
    }
    return <StyledAvatar>{userName?.substring(0, 1)}</StyledAvatar>;
  }, [
    userAvatar,
    userName,
  ]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    // Update weight goal
    await nutritionProfileDoc?.updateFields({
      weeklyWeightGoal: selectedWeightGoal,
      lastUpdatedAt: getFirebase().default.firestore.FieldValue.serverTimestamp(),
    });
    // Save previous goals
    await macroGoalsDoc?.saveHistory(
      mealPlanAssignmentDoc?.mealPlanId,
      mealPlanAssignmentDoc?.name,
      mealPlanAssignmentDoc?.macroAverages,
    );

    // If its recommended, remove macroAverages property as it is calculated later using calories.
    let newNutritionalGoals = nutritionalGoals;
    if (nutritionalGoals?.isRecommended) {
      const {
        isRecommended,
        macroAverages,
        ...rest
      } = nutritionalGoals;
      newNutritionalGoals = rest;
    }

    // Update nutritional goals
    await macroGoalsDoc?.updateMacroGoals(newNutritionalGoals);

    let assignmentId;

    // Update meal plan assignment and assignment note, create doc when assigning for the first time
    if (!mealPlanAssignmentDoc) {
      const newDoc = await MealPlanAssignment.addNewAssignment({
        user,
        mealPlanData: newMealPlanData,
        coach: coachId,
        approvedBy: authUserId || '',
        // The page's selected product, so a second product's assignment lands on that product.
        ...(await MealPlanAssignment.resolveAssignmentProduct(userDoc, selectedProductId)),
      });
      assignmentId = newDoc?.id;
      await newDoc.setCustomMealPlanIntroMessage(assignmentNote);
      logCoachingActivity(CoachingActivity.ASSIGNED_MEAL_PLAN, {
        mealPlanId: newMealPlanData.mealPlanId,
        clientId: user,
      });
    } else {
      assignmentId = await mealPlanAssignmentDoc.updateMealPlan(newMealPlanData, { approvedBy: authUserId || '' });
      await mealPlanAssignmentDoc.setCustomMealPlanIntroMessage(assignmentNote);
      logCoachingActivity(CoachingActivity.UPDATED_ASSIGNED_MEAL_PLAN, {
        mealPlanId: newMealPlanData.mealPlanId,
        clientId: user,
      });
    }

    // Log the time taken to assign the meal plan
    logMealPlanAssignmentTime({
      mealPlanAssignmentId: assignmentId,
      isGeneratedByAI: isAIGenerated,
      isRefresh: !!mealPlanAssignmentDoc,
      clientId: user,
    });

    if (isComponentMountedRef.current) {
      // Redirect to feed if the assignment was triggered from there
      if (isRoutedFromFeed) {
        history.goBack();
      } else {
        navigateToAssignments({
          clearSearch: true,
          replace: true,
        });
      }
    }
  }, [
    selectedWeightGoal,
    nutritionalGoals,
    newMealPlanData,
    assignmentNote,
    nutritionProfileDoc,
    macroGoalsDoc,
    mealPlanAssignmentDoc,
    userDoc,
    selectedProductId,
    isComponentMountedRef,
    navigateToAssignments,
    user,
    coachId,
    history,
    isRoutedFromFeed,
    logCoachingActivity,
    isAIGenerated,
    logMealPlanAssignmentTime,
    authUserId,
  ]);

  // Ensure context has assignment doc available when AI flow is used
  useEffect(() => {
    if (isAIGenerated && !mealPlanAssignmentDoc) {
      fetchMealPlanAssignmentDoc?.();
    }
  }, [isAIGenerated, mealPlanAssignmentDoc, fetchMealPlanAssignmentDoc]);

  const isSaveDisabled = useMemo(() => {
    // Meal times are valid if all meal times are not empty
    const isMealTimesValid = newMealPlanData.mealTimes && !newMealPlanData.mealTimes.some((mealTime) => (
      mealTime.meals && mealTime.meals.length === 0
    ));
    return !selectedWeightGoal || !nutritionalGoals?.updatedMacroAveragesAt || !isMealTimesValid;
  }, [
    selectedWeightGoal,
    nutritionalGoals,
    newMealPlanData,
  ]);

  if (!user) {
    return <Redirect to={getTabPath(MealPlannerType.CLIENT_MEAL_ASSIGNMENTS)} />;
  }

  return (
    <Container>
      <UserInfoContainer>
        {renderAvatar()}
        <UserInfoTitle>
          {/*
            Shown for every client, not only multi-product ones: this page writes a plan, and
            the product it lands on cannot be corrected afterwards without reassigning.
          */}
          <TitleRow>
            {format(texts.title, { userName })}
            {!!assignmentProductName && <InfoTag>{assignmentProductName}</InfoTag>}
          </TitleRow>
          <TitleIconsContainer>
            <ChatButton
              userId={userId}
              popperPlacement="right-start"
              iconVariant="secondary"
              smallButton
            />
            <ClientQuickInfoButton userId={userId} />
          </TitleIconsContainer>
        </UserInfoTitle>
      </UserInfoContainer>
      <SectionContainer>
        <UserPhysicalAttributes
          onSelectWeightGoal={setSelectedWeightGoal}
        />
      </SectionContainer>
      <SectionContainer>
        <UserNutritionalGoals
          onSelectNutritionalGoals={setNutritionalGoals}
        />
      </SectionContainer>
      <SectionContainer>
        <UserMealPlan
          userId={user}
          scaleMeals={!!nutritionalGoals?.totalDailyCalories}
          totalDailyCalories={nutritionalGoals?.totalDailyCalories}
          onIsAIGeneratedChange={setIsAIGenerated}
          macrosPercentages={nutritionalGoals?.macroAverages?.percentages
            || macroGoalsDoc?.macroAverages?.percentages
            || newMealPlanData?.macroAverages?.percentages}
          defaultAction={!mealPlanAssignmentDoc
            ? MealPlanAssignOption.NEW_MEAL_PLAN
            : MealPlanAssignOption.USE_PREVIOUS_MEAL_PLAN}
          feedbackHandler={feedbackHandler}
          setFeedbackHandler={setFeedbackHandler}
        />
      </SectionContainer>
      <SectionContainer>
        <PrimaryButton
          icon={<CheckIcon />}
          onClick={handleSave}
          disabled={isSaving || isSaveDisabled}
        >
          {texts.approveMealPlan}
        </PrimaryButton>
        {
          isAIGenerated
          && (
            <ContentContainer>
              <ItemContainer>
                <FeedbackButton
                  onClick={() => setFeedbackHandler(true)}
                  size="medium"
                  startIcon={<StarIcon />}
                >
                  {texts.aiMealPlanFeedbackButton}
                </FeedbackButton>
              </ItemContainer>
              <ItemContainer>
                <Label>{texts.aiMealPlanFeedback}</Label>
              </ItemContainer>
            </ContentContainer>
          )
        }
      </SectionContainer>
    </Container>
  );
};

export default compose(
  withMealPlanContextReady,
  withMealPlanAssignmentContextProvider,
  withMealPlanAssignmentContextReady,
)(MealPlanRefresh);
